import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as api from './blog-categories.api';

import type { BlogCategoryListQuery } from './blog-categories.api';
import type {
  CreateWebsiteBlogCategoryRequest,
  UpdateWebsiteBlogCategoryRequest,
} from '@utazzo/shared';

const QK = {
  list: (q: BlogCategoryListQuery) => ['blog-categories', 'list', q] as const,
};

export const useBlogCategoriesList = (query: BlogCategoryListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => api.listBlogCategories(query),
    staleTime: 30_000,
  });

const extractMessage = (err: unknown): string | undefined => {
  if (!err || typeof err !== 'object') return undefined;
  const e = err as { response?: { data?: { message?: string | string[] } }; message?: string };
  const m = e.response?.data?.message;
  if (Array.isArray(m)) return m.join('; ');
  return m ?? e.message;
};

export const useCreateBlogCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateWebsiteBlogCategoryRequest) => api.createBlogCategory(payload),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ['blog-categories', 'list'] });
      toast.success(`Category ${c.name} created`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to create category'),
  });
};

export const useUpdateBlogCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateWebsiteBlogCategoryRequest }) =>
      api.updateBlogCategory(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['blog-categories', 'list'] });
      toast.success('Category updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update category'),
  });
};

export const useDeleteBlogCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteBlogCategory(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['blog-categories', 'list'] });
      toast.success('Category deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete category'),
  });
};

export const useBulkDeleteBlogCategories = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => api.bulkDeleteBlogCategories(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['blog-categories', 'list'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete categories'),
  });
};

export const useBulkSetBlogCategoriesStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      api.bulkSetBlogCategoriesStatus(ids, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['blog-categories', 'list'] }),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update category status'),
  });
};
